import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExportButton from "@/components/ExportButton";
import EmptyState from "@/components/EmptyState";
import { Trophy, ArrowLeft, ArrowsCounterClockwise } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const METRICS = [
    { key: "commission", label: "Commission earned" },
    { key: "payments_received", label: "Payments received" },
    { key: "portal_fee", label: "Portal fees" },
    { key: "combined", label: "Combined (commission + portal fee)" },
];

/**
 * v1.21.0 — Highest Earning Parties Leaderboard. Rank by any metric across
 * a rolling window or a date range. Excludes suspense rows.
 */
export default function Leaderboard() {
    const navigate = useNavigate();
    const [metric, setMetric] = useState("commission");
    const [months, setMonths] = useState(12);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [limit, setLimit] = useState(25);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = { metric, limit };
            if (dateFrom || dateTo) {
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
            } else {
                params.months = months;
            }
            const { data } = await api.get("/reports/leaderboard", { params });
            setData(data);
            if (!dateFrom && data.date_from) setDateFrom(data.date_from);
            if (!dateTo && data.date_to) setDateTo(data.date_to);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [metric, months]);

    const rows = data?.rows || [];
    const max = Math.max(1, ...rows.map((r) => Number(r[metric] || 0)));

    return (
        <div className="space-y-6" data-testid="leaderboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md">
                            <Trophy size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Highest Earning Parties</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Top-ranked parties by the metric of your choice. Suspense entries are excluded.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <ExportButton
                        endpoint={{ pdf: "/export/leaderboard.pdf", xlsx: "/export/leaderboard.xlsx" }}
                        params={{ metric, months: (dateFrom || dateTo) ? undefined : months, date_from: dateFrom || undefined, date_to: dateTo || undefined, limit }}
                        filenameBase={`leaderboard-${metric}`}
                        testId="leaderboard-export"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-5">
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Rank by</Label>
                    <Select value={metric} onValueChange={setMetric}>
                        <SelectTrigger data-testid="leaderboard-metric" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {METRICS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Window</Label>
                    <Select value={String(months)} onValueChange={(v) => { setMonths(Number(v)); setDateFrom(""); setDateTo(""); }}>
                        <SelectTrigger data-testid="leaderboard-months" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {[1, 3, 6, 12, 24].map((m) => <SelectItem key={m} value={String(m)}>Last {m} month{m === 1 ? "" : "s"}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">From</Label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="leaderboard-from" className="mt-1 h-9" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">To</Label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="leaderboard-to" className="mt-1 h-9" />
                </div>
                <div className="flex items-end gap-2">
                    <button onClick={load} disabled={loading} data-testid="leaderboard-apply"
                        className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                        <ArrowsCounterClockwise size={13} className={loading ? "animate-spin" : ""} /> Apply
                    </button>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {rows.length === 0 && !loading ? (
                    <div className="p-6"><EmptyState icon={Trophy} title="No results" description="No party activity in this range." /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="leaderboard-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-right w-14">Rank</th>
                                    <th className="px-3 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-right">Payments received</th>
                                    <th className="px-3 py-2 text-right">Commission</th>
                                    <th className="px-3 py-2 text-right">Portal fee</th>
                                    <th className="px-3 py-2 text-right">Combined</th>
                                    <th className="px-3 py-2 text-left w-64">Share of top-1</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r, i) => {
                                    const val = Number(r[metric] || 0);
                                    const pct = Math.round((val / max) * 100);
                                    return (
                                        <tr key={r.party_id || i} data-testid={`leaderboard-row-${r.party_id}`} className="hover:bg-slate-50">
                                            <td className="px-3 py-2 text-right font-mono font-bold text-slate-700">#{i + 1}</td>
                                            <td className="px-3 py-2">
                                                <button type="button"
                                                    onClick={() => navigate(`/admin/reports?partyId=${r.party_id}`)}
                                                    className="text-left font-medium text-blue-700 hover:underline">
                                                    {r.party_name}
                                                </button>
                                                {r.contact_person && <div className="text-[10px] text-slate-500">{r.contact_person}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-800">{fmt(r.payments_received)}</td>
                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-blue-800">{fmt(r.commission)}</td>
                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-800">{fmt(r.portal_fee)}</td>
                                            <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-slate-900">{fmt(r.combined)}</td>
                                            <td className="px-3 py-2">
                                                <div className="relative h-4 w-full overflow-hidden rounded-md bg-slate-100">
                                                    <div className="absolute inset-y-0 left-0 rounded-md bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${pct}%` }} />
                                                </div>
                                                <div className="mt-0.5 text-[10px] font-mono text-slate-500">{pct}%</div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
